import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import AppointmentCounts from "../components/AppointmentCounts";
import AppointmentFilter from "../components/AppointmentFilter";
import AppointmentTr from "../components/AppointmentTr";
import AppointmentTableHead from "../components/AppointmentTableHead";
import AppointmentMobileCard from "../components/AppointmentMobileCard";
import baseUrl from "../../../../api/baseUrl";
import { useDispatch, useSelector } from "react-redux";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { RootState } from "../../../../redux/store";
import {
  setMyAppointment,
  setMyAppointmentCount,
} from "../../../../redux/appointment/appointmentAction";
import BottomTab from "../../../../components/pagination/BottomTab";
import { openPatientSummaryInNewTab } from "../../../../utils/openPatientSummaryInNewTab";
import { getTodayWeekDay } from "../../../nextQueue/nextQueueUtils";
import { fetchDefaultSessionForToday } from "../../../../utils/resolveCurrentSession";
import { resolveListStatusHeader } from "../components/appointmentViewShared";

const COUNT_TO_STATUS: Record<string, string> = {
  all: "pending",
  all_exc_cancelled_noshow: "all_exc_cancelled_noshow",
  active: "active",
  completed: "complete",
  cancelled_noshow: "cancelled_noshow",
  upcoming: "upcoming",
  in_queue: "in_queue",
  ongoing: "ongoing",
  complete: "complete",
  cancelled: "cancelled",
  no_show: "no_show",
  pre_booked: "pre_booked",
  walk_in: "walk_in",
  call_in: "call_in",
  online: "online",
  pending: "pending",
};

const STATUS_TO_COUNT: Record<string, string> = {
  pending: "all",
  all_exc_cancelled_noshow: "all_exc_cancelled_noshow",
  active: "active",
  complete: "complete",
  completed: "completed",
  cancelled_noshow: "cancelled_noshow",
  upcoming: "upcoming",
  in_queue: "in_queue",
  inqueue: "in_queue",
  ongoing: "ongoing",
  cancelled: "cancelled",
  canceled: "cancelled",
  no_show: "no_show",
  noshow: "no_show",
  pre_booked: "pre_booked",
  schedule: "pre_booked",
  walk_in: "walk_in",
  walkin: "walk_in",
  call_in: "call_in",
  callin: "call_in",
  online: "online",
};

const FILTER_LABELS: Record<string, string> = {
  pending: "All (incl. Cancel & No-Show)",
  all: "All (incl. Cancel & No-Show)",
  all_exc_cancelled_noshow: "All (excl. Cancel & No-Show)",
  active: "Active",
  complete: "Complete",
  completed: "Completed",
  cancelled_noshow: "Cancelled & No-Show",
  upcoming: "Upcoming",
  in_queue: "In Queue",
  ongoing: "Ongoing",
  cancelled: "Cancelled",
  no_show: "No-Show",
  pre_booked: "Schedule",
  walk_in: "Walk-in",
  call_in: "Call-in",
  online: "Online",
};

function mergeSessionNames(...lists: (string[] | undefined)[]): string[] {
  const set = new Set<string>();
  for (const list of lists) {
    for (const name of list ?? []) {
      const trimmed = String(name).trim();
      if (trimmed) set.add(trimmed);
    }
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

function buildAppointmentParams(
  all: string,
  session: string,
  filterDate: string
): Record<string, string> | undefined {
  const params: Record<string, string> = {};
  if (session.trim()) params.sessionName = session.trim();
  if (all === "all" && filterDate.trim()) params.filterDate = filterDate.trim();
  return Object.keys(params).length ? params : undefined;
}

function normalizeCountsPayload(data: any) {
  return {
    ...data,
    by_view: data?.by_view,
    by_status: data?.by_status,
    by_source: data?.by_source,
    appointments: data?.appointments ?? data?.total,
    cancelled: data?.cancelled ?? data?.canceled,
    completed: data?.completed ?? data?.complete,
    complete: data?.complete ?? data?.completed,
    pending: data?.pending ?? data?.upcoming,
    inqueue: data?.inqueue ?? data?.in_queue,
    in_queue: data?.in_queue ?? data?.inqueue,
    waiting: data?.waiting,
    reschudle: data?.reschudle,
    ongoing: data?.ongoing,
    noshow: data?.noshow ?? data?.no_show,
    no_show: data?.no_show ?? data?.noshow,
    walkin: data?.walkin ?? data?.walk_in,
    walk_in: data?.walk_in ?? data?.walkin,
    callin: data?.callin ?? data?.call_in,
    call_in: data?.call_in ?? data?.callin,
    assigned: data?.assigned,
    appointment: data?.appointment ?? data?.upcoming ?? data?.pending,
    upcoming: data?.upcoming ?? data?.appointment ?? data?.pending,
    schedule: data?.schedule ?? data?.pre_booked,
    pre_booked: data?.pre_booked ?? data?.schedule,
    online: data?.online,
    active: data?.by_view?.active ?? data?.active,
  };
}

function YourAppointment() {
  const [all, setAll] = useState("today");
  const [selectedView, setSelectedView] = useState<string>(
    "all_exc_cancelled_noshow"
  );
  const [selectedStatusCard, setSelectedStatusCard] = useState<string>("");
  const [selectedSourceChip, setSelectedSourceChip] = useState<string>("");
  const [selectedSession, setSelectedSession] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [sessions, setSessions] = useState<string[]>([]);
  const [sessionDefaultReady, setSessionDefaultReady] = useState(false);
  const [counts, setCounts] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const userPickedSessionRef = useRef(false);
  const dispatch = useDispatch();
  const appointment = useSelector((state: RootState) => state.appointment);

  const selectedOption = useMemo(
    () =>
      resolveListStatusHeader({
        view: selectedView,
        statusCard: selectedStatusCard,
        sourceChip: selectedSourceChip,
      }),
    [selectedView, selectedStatusCard, selectedSourceChip]
  );

  const getAppointments = useCallback(
    (
      status: string,
      day: string,
      session: string,
      filterDate: string,
      newPage?: number
    ) => {
      setIsLoading(true);
      return baseUrl
        .get("/appointment/findAllAppointmentDentistId", {
          headers: { status, day, page: newPage ?? 1 },
          params: buildAppointmentParams(day, session, filterDate),
        })
        .then((response) => {
          const rows = Array.isArray(response.data?.data)
            ? response.data.data
            : [];
          setCounts(response.data?.count ?? 0);
          dispatch(setMyAppointment(rows, false));
          setSessions((prev) =>
            mergeSessionNames(
              prev,
              rows.map((row: { sessionName?: string }) => row?.sessionName)
            )
          );
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [dispatch]
  );

  const loadSessionNames = useCallback((day: string, filterDate: string) => {
    baseUrl
      .get("/appointment/dentistAppointmentSessionNames", {
        headers: { day },
        params:
          day === "all" && filterDate.trim()
            ? { filterDate: filterDate.trim() }
            : undefined,
      })
      .then((response) => {
        const names = Array.isArray(response.data?.data)
          ? response.data.data
          : [];
        setSessions((prev) => mergeSessionNames(prev, names));
      })
      .catch((error) => {
        console.error(error);
      });
  }, []);

  const fetchCounts = useCallback(
    (day: string, filterDate: string, session: string) => {
      return baseUrl
        .get("/appointment/appointmentscountsbyId", {
          headers: {
            status: true,
            day,
            filterDate: day === "all" && filterDate.trim() ? filterDate.trim() : "",
          },
          params: session.trim()
            ? { sessionName: session.trim() }
            : undefined,
        })
        .then((response) => {
          dispatch(setMyAppointmentCount(normalizeCountsPayload(response.data)));
        })
        .catch((error) => {
          console.error(error);
        });
    },
    [dispatch]
  );

  useEffect(() => {
    loadSessionNames(all, selectedDate);
  }, [all, selectedDate, loadSessionNames]);

  useEffect(() => {
    if (all !== "today") {
      setSessionDefaultReady(true);
      return;
    }
    if (userPickedSessionRef.current) {
      setSessionDefaultReady(true);
      return;
    }

    let cancelled = false;
    setSessionDefaultReady(false);

    (async () => {
      // Only auto-pick a session that exists on today's appointments.
      // General Hours may return "General" (or similar) which hides Morning/Evening rows.
      let availableNames: string[] = [];
      try {
        const res = await baseUrl.get(
          "/appointment/dentistAppointmentSessionNames",
          { headers: { day: "today" } }
        );
        availableNames = Array.isArray(res.data?.data) ? res.data.data : [];
        if (!cancelled) {
          setSessions((prev) => mergeSessionNames(prev, availableNames));
        }
      } catch {
        /* keep All Sessions if names cannot be loaded */
      }

      const defaultSession = await fetchDefaultSessionForToday(
        baseUrl,
        getTodayWeekDay(),
        availableNames
      );
      if (cancelled) return;
      if (defaultSession) {
        setSelectedSession(defaultSession);
      } else {
        setSelectedSession("");
      }
      setSessionDefaultReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [all]);

  useEffect(() => {
    if (!sessionDefaultReady) return;
    getAppointments(selectedOption, all, selectedSession, selectedDate, 1);
  }, [
    getAppointments,
    selectedOption,
    all,
    selectedSession,
    selectedDate,
    sessionDefaultReady,
  ]);

  useEffect(() => {
    fetchCounts(all, selectedDate, selectedSession);
  }, [fetchCounts, all, selectedDate, selectedSession]);

  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const countKey = STATUS_TO_COUNT[value] ?? value;
    if (
      countKey === "all" ||
      countKey === "all_exc_cancelled_noshow" ||
      countKey === "active" ||
      countKey === "completed" ||
      countKey === "cancelled_noshow"
    ) {
      setSelectedView(countKey);
      setSelectedStatusCard("");
      setSelectedSourceChip("");
    } else if (
      ["upcoming", "in_queue", "ongoing", "complete", "cancelled", "no_show"].includes(
        countKey
      )
    ) {
      setSelectedStatusCard(countKey);
    } else if (
      ["pre_booked", "walk_in", "call_in", "online"].includes(countKey)
    ) {
      setSelectedSourceChip(countKey);
    }
    getAppointments(
      COUNT_TO_STATUS[countKey] ?? value,
      all,
      selectedSession,
      selectedDate,
      1
    );
  };

  const handleDay = (event: React.ChangeEvent<HTMLInputElement>) => {
    const day = event.target.value;
    const nextDate = day === "all" ? selectedDate : "";
    setAll(day);
    userPickedSessionRef.current = false;
    setSelectedSession("");
    if (day !== "all") setSelectedDate("");
    if (day === "today") {
      setSessionDefaultReady(false);
      return;
    }
    setSessionDefaultReady(true);
    getAppointments(selectedOption, day, "", nextDate, 1);
  };

  const handleSessionChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const session = event.target.value;
    userPickedSessionRef.current = true;
    setSelectedSession(session);
    getAppointments(selectedOption, all, session, selectedDate, 1);
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextDate = event.target.value;
    setSelectedDate(nextDate);
    userPickedSessionRef.current = false;
    setSelectedSession("");
    getAppointments(selectedOption, all, "", nextDate, 1);
  };

  const handleViewSelect = useCallback(
    (viewKey: string) => {
      setSelectedView(viewKey);
      setSelectedStatusCard("");
      const header = resolveListStatusHeader({
        view: viewKey,
        statusCard: "",
        sourceChip: selectedSourceChip,
      });
      getAppointments(header, all, selectedSession, selectedDate, 1);
    },
    [getAppointments, all, selectedSession, selectedDate, selectedSourceChip]
  );

  const handleStatusCardSelect = useCallback(
    (statusKey: string) => {
      setSelectedStatusCard(statusKey);
      const header = resolveListStatusHeader({
        view: selectedView,
        statusCard: statusKey,
        sourceChip: selectedSourceChip,
      });
      getAppointments(header, all, selectedSession, selectedDate, 1);
    },
    [
      getAppointments,
      all,
      selectedSession,
      selectedDate,
      selectedView,
      selectedSourceChip,
    ]
  );

  const handleSourceChipSelect = useCallback(
    (sourceKey: string) => {
      setSelectedSourceChip(sourceKey);
      const header = resolveListStatusHeader({
        view: selectedView,
        statusCard: selectedStatusCard,
        sourceChip: sourceKey,
      });
      getAppointments(header, all, selectedSession, selectedDate, 1);
    },
    [
      getAppointments,
      all,
      selectedSession,
      selectedDate,
      selectedView,
      selectedStatusCard,
    ]
  );

  const refreshWithCounts = useCallback(() => {
    fetchCounts(all, selectedDate, selectedSession);
    getAppointments(selectedOption, all, selectedSession, selectedDate, 1);
  }, [
    fetchCounts,
    getAppointments,
    selectedOption,
    all,
    selectedSession,
    selectedDate,
  ]);

  const handleRefresh = useCallback(() => {
    refreshWithCounts();
  }, [refreshWithCounts]);

  const handleOngoingStatus = useCallback((patientId: string) => {
    openPatientSummaryInNewTab(patientId);
  }, []);

  const appointmentRows = useMemo(
    () =>
      appointment?.myappointment?.map((item: any, index: number) => (
        <AppointmentTr
          appointmStatus={refreshWithCounts}
          key={item?._id ?? index}
          item={item}
          showAddTreatmentButton={false}
          showPatientDetailsButton={true}
          showViewAppointmentButton={false}
          showPrintTokenButton={false}
          showDoctorColumn={false}
          statusAsButtons
          showQueueActions={false}
          myAppointmentLayout
          onOngoingStatus={handleOngoingStatus}
        />
      )),
    [appointment, handleOngoingStatus, refreshWithCounts]
  );

  const onPageChange = useCallback(
    (newPage: number) => {
      getAppointments(
        selectedOption,
        all,
        selectedSession,
        selectedDate,
        newPage
      );
    },
    [getAppointments, all, selectedOption, selectedSession, selectedDate]
  );

  const activeStatusLabel =
    FILTER_LABELS[selectedSourceChip] ||
    FILTER_LABELS[selectedStatusCard] ||
    FILTER_LABELS[selectedView] ||
    FILTER_LABELS[selectedOption] ||
    "Active";

  const hasExtraFilters =
    Boolean(selectedSession) || (all === "all" && Boolean(selectedDate));

  const emptyState = (
    <div className="flex flex-col items-center justify-center py-14 px-6 text-center">
      <div className="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-700/80 flex items-center justify-center mb-4">
        <SearchOffIcon
          className="text-slate-400 dark:text-slate-500"
          sx={{ fontSize: 32 }}
        />
      </div>
      <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 mb-1">
        No appointments found
      </h3>
      <p className="text-sm text-slate-500 dark:text-slate-400 max-w-sm">
        No {activeStatusLabel.toLowerCase()} appointments match your current
        filters.
      </p>
      {hasExtraFilters ? (
        <button
          type="button"
          onClick={() => {
            userPickedSessionRef.current = false;
            setSelectedSession("");
            setSelectedDate("");
            if (all === "today") {
              setSessionDefaultReady(false);
              return;
            }
            getAppointments(selectedOption, all, "", "", 1);
          }}
          className="mt-4 px-4 py-2 rounded-lg text-sm font-medium text-indigo-600 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/30 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors"
        >
          Clear all filters
        </button>
      ) : null}
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="bg-white dark:bg-slate-800 rounded-xl md:rounded-2xl shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 p-3 md:p-5">
        <AppointmentFilter
          variant="toolbar"
          hideStatusFilter
          selectedOption={selectedOption}
          all={all}
          handleOptionChange={handleOptionChange}
          handleDay={handleDay}
          sessions={sessions}
          selectedSession={selectedSession}
          handleSessionChange={handleSessionChange}
          showSessionFilter
          selectedDate={selectedDate}
          handleDateChange={handleDateChange}
        />

        <div className="mt-4 mb-3">
          <AppointmentCounts
            count={appointment?.myappointmentCount}
            compact
            activeView={selectedView}
            activeStatus={selectedStatusCard}
            activeSource={selectedSourceChip}
            onViewSelect={handleViewSelect}
            onStatusSelect={handleStatusCardSelect}
            onSourceSelect={handleSourceChipSelect}
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 mb-3 px-0.5">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            <span className="font-semibold text-slate-800 dark:text-slate-100 tabular-nums">
              {isLoading ? "…" : counts}
            </span>{" "}
            {counts === 1 ? "result" : "results"}
            <span className="text-slate-400 dark:text-slate-500 mx-1.5">·</span>
            {activeStatusLabel}
            {all === "today" ? (
              <span className="text-slate-400 dark:text-slate-500"> · Today</span>
            ) : all === "tomorrow" ? (
              <span className="text-slate-400 dark:text-slate-500"> · Tomorrow</span>
            ) : selectedDate ? (
              <span className="text-slate-400 dark:text-slate-500">
                {" "}
                · {selectedDate}
              </span>
            ) : (
              <span className="text-slate-400 dark:text-slate-500"> · All dates</span>
            )}
            {selectedSession ? (
              <span className="text-slate-400 dark:text-slate-500">
                {" "}
                · {selectedSession}
              </span>
            ) : null}
          </p>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isLoading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-700/80 border border-slate-200 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-600 disabled:opacity-60 transition-all"
            title="Refresh appointments"
          >
            <RefreshIcon
              sx={{ fontSize: 16 }}
              className={isLoading ? "animate-spin text-indigo-500" : ""}
            />
            Refresh
          </button>
        </div>

        <div className="hidden md:block relative">
          <div className="overflow-auto max-h-[min(68vh,720px)] rounded-xl border border-slate-200 dark:border-slate-600">
            <table className="w-full">
              <AppointmentTableHead sticky myAppointmentLayout />
              <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-100 dark:divide-slate-700/80 [&>tr:nth-child(even)]:bg-slate-50/60 dark:[&>tr:nth-child(even)]:bg-slate-800/50">
                {appointmentRows}
              </tbody>
            </table>
            {!isLoading && appointment?.myappointment?.length === 0 ? emptyState : null}
          </div>
          {isLoading && (appointment?.myappointment?.length ?? 0) === 0 ? (
            <TableSkeleton />
          ) : null}
        </div>

        <div className="md:hidden space-y-3">
          {isLoading && (appointment?.myappointment?.length ?? 0) === 0 ? (
            <MobileSkeleton />
          ) : null}
          {appointment?.myappointment?.map((item: any, index: number) => (
            <AppointmentMobileCard
              key={item?._id ?? index}
              item={item}
              onStatusChange={refreshWithCounts}
              onOngoingStatus={handleOngoingStatus}
              showAddTreatmentButton={false}
              showPatientDetailsButton={true}
              showViewAppointmentButton={false}
              showDoctorColumn={false}
              tokenOnly
            />
          ))}
          {!isLoading && appointment?.myappointment?.length === 0 ? emptyState : null}
        </div>

        {all !== "today" ? (
          <BottomTab
            key={`${all}-${selectedOption}-${selectedSession}-${selectedDate}`}
            title="Appointment"
            count={counts}
            onPageChange={onPageChange}
          />
        ) : null}
      </div>
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="absolute inset-0 bg-white/60 dark:bg-slate-800/60 backdrop-blur-[1px] flex flex-col gap-0">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="h-14 border-b border-slate-100 dark:border-slate-700/80 animate-pulse bg-gradient-to-r from-slate-100 via-slate-50 to-slate-100 dark:from-slate-700 dark:via-slate-800 dark:to-slate-700"
          style={{ animationDelay: `${i * 80}ms` }}
        />
      ))}
    </div>
  );
}

function MobileSkeleton() {
  return (
    <>
      {Array.from({ length: 3 }).map((_, i) => (
        <div
          key={i}
          className="h-36 rounded-xl border border-slate-200 dark:border-slate-600 animate-pulse bg-slate-100 dark:bg-slate-700/50"
        />
      ))}
    </>
  );
}

export default YourAppointment;
